// Self-contained OKLab/OKLCH shading. OKLab is perceptually uniform, so setting
// lightness while keeping chroma + hue yields "the same color, lighter/darker"
// for any hue — unlike HSLuv setLightness, which drifts hue (purple→pink) and
// saturation (teal→wash). Coefficients: Björn Ottosson's OKLab (public domain).
// A scoped slice of the culori-in-core ticket; no external dependency.

function srgbToLinear(c) {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
}

function linearToSrgb(x) {
  const c = x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
  return Math.max(0, Math.min(255, Math.round(c * 255)));
}

function parseHex(hex) {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
        .split('')
        .map(c => c + c)
        .join('')
      : h;
  return [0, 2, 4].map(i => parseInt(full.substring(i, i + 2), 16));
}

function toHex2(n) {
  const h = n.toString(16);
  return h.length === 1 ? `0${h}` : h;
}

// hex -> {l, c, h}  (l in 0–1, c ~0–0.4, h in degrees 0–360)
export function hexToOklch(hex) {
  const [r8, g8, b8] = parseHex(hex);
  const r = srgbToLinear(r8);
  const g = srgbToLinear(g8);
  const b = srgbToLinear(b8);

  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const bb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

  const c = Math.sqrt(a * a + bb * bb);
  let h = (Math.atan2(bb, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l: L, c, h };
}

function oklchToHex({ l, c, h }) {
  const hr = (h * Math.PI) / 180;
  const tryChroma = chroma => {
    const a = chroma * Math.cos(hr);
    const b = chroma * Math.sin(hr);
    const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = l - 0.0894841775 * a - 1.291485548 * b;
    const lc = l_ * l_ * l_;
    const mc = m_ * m_ * m_;
    const sc = s_ * s_ * s_;
    const r = 4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc;
    const g = -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc;
    const bch = -0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc;
    return [r, g, bch];
  };
  let chroma = c;
  let rgb = tryChroma(chroma);
  for (let i = 0; i < 12 && rgb.some(v => v < -0.0001 || v > 1.0001); i++) {
    chroma *= 0.8;
    rgb = tryChroma(chroma);
  }
  const [r, g, b] = rgb.map(linearToSrgb);
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;
}

// When a shade LIGHTENS the source, fade its chroma toward white so the result
// is a true tint (a muted lighter version) rather than the same chroma at a
// higher lightness — which reads as a vivid, "not monochromatic" color (e.g. a
// muted green lightening into a baby-blue). 0 = keep full chroma; 1 = fully grey
// at white. Darkening is unaffected (dark shades keep full chroma — that path
// always looked right). Hue is preserved in every case (monochromatic).
const CHROMA_FADE_ON_LIGHTEN = 0.6;

// Return `hex` shaded to `targetLightness` (0–100 scale, matching the dial's
// HSLuv lightness usage), keeping hue. Lightening fades chroma toward a tint
// (see CHROMA_FADE_ON_LIGHTEN); darkening keeps chroma.
export function shadeHex(hex, targetLightness) {
  const { l: sourceL, c, h } = hexToOklch(hex);
  const l = Math.max(0, Math.min(1, targetLightness / 100));
  let chroma = c;
  if (l > sourceL && sourceL < 1) {
    const lightenFrac = (l - sourceL) / (1 - sourceL); // 0 at source → 1 at white
    chroma = c * (1 - CHROMA_FADE_ON_LIGHTEN * lightenFrac);
  }
  return oklchToHex({ l, c: chroma, h });
}
